/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik } from "formik"
import { useLocation } from "react-router-dom"
import { HttpStatusCode } from "axios";

import ArrowIcon from "@/assets/ArrowIcon";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import Loader from "@/components/Layouts/Loader";
import SelectRoleType from "@/components/Selects/SelectRoleType";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { addRoleSchema } from "@/schemas/addRoleSchema";
import { getRoleById, postRole, putRole } from "@/services/roleService";
import { getDataRef } from "@/services/refDataService";

const EditRole = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const location = useLocation();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id } = location.state || {};

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [listType, setListType] = useState<any[]>([]);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = (data: any) => {
        setTitle("Save This Role?");
        setConfirmationTest("Are you sure you want to save?");
        setConfirmButtonText("Save Role");
        setCancelButtonText("Cancel");
        onOpen();
        setIsSubmitting(true);
        setDataEdit(data);
    }

    const generateDataEdit = () => {
        const data = {
            roleId: dataEdit?.id || '',
            roleName: dataEdit?.name || '',
            description: dataEdit?.desc || '',
            roleType: dataEdit?.type || '',
        };

        if (id){
            return { ...data, modifiedBy: name };
        }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            history.back();
            return;
        }

        setIsLoading(true);
        const obj = generateDataEdit();
        let response
        if (id){
            response = await putRole(obj);
        } else {
            response = await postRole(obj);
        }
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            toast({
                title: 'Role saved.',
                description: "We've saved this role.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                history.back();
                setIsLoading(false);
            }, 1000)
        } else {
            toast({
                title: 'Role failed to save.',
                description: response?.message || "We could not save this role.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const getDataDetail = async () => {
        const response = await getRoleById(id) as any;
        if (response?.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);
            setIsLoading(false);
        } else {
            setIsLoading(false);
            toast({
                title: 'Failed to load role data.',
                description: response.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const getListType = async (value: string) => {
        const response = await getDataRef(value) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                label: item.name,
                value: item.value,
            }));

            setListType(formattedData);
        }
    }

    useEffect(() => {
        getListType('roletype');
    }, []);

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id]);

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                id: dataDetail?.roleId || '',
                                name: dataDetail?.roleName || '',
                                desc: dataDetail?.description || '',
                                type: dataDetail?.roleType || ''
                            }}
                            validationSchema={addRoleSchema}
                            onSubmit={(value) => {
                                handleSubmit(value);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Field name='id'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.id && form.touched.id)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">ID<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Role ID' className="form-input" disabled={Boolean(id)} />
                                                        <FormErrorMessage>{String(form.errors.id)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='name'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.name && form.touched.name)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Name' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.name)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='desc'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.desc && form.touched.desc)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Desc<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Desc' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.desc)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='type'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.type && form.touched.type)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectRoleType name='type' handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} defaultValue={props.values.type} hasError={form.errors.type && form.touched.type} options={listType} />
                                                        <FormErrorMessage>{String(form.errors.type)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor='white'
                                            borderColor='#D0D5DD'
                                            isLoading={isSubmitting}
                                            marginRight={5}
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Save Role
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default EditRole;
